/**
 * TtsPlayer — plays Guide's spoken responses via Web Audio API.
 *
 * Shares an AudioContext with MusicPlayer. Both run through separate
 * GainNodes so TTS and music can play simultaneously on ALL platforms
 * (including iOS) with independent volume control:
 *
 *   AudioContext
 *     ├─ TTS GainNode    → fade-in 1.2s, full volume
 *     └─ Music GainNode  → ducks to 15% during speech
 *
 * No more YouTube pause/resume — music just gets quieter during TTS
 * and ramps back up when TTS ends.
 */
export class TtsPlayer {
    constructor(_config, _bus, _queue, _music) {
        this._config = _config;
        this._bus = _bus;
        this._queue = _queue;
        this._music = _music;
        this._ctx = null;
        this._gain = null;
        this._analyser = null;
        this._currentSource = null;
        this._primed = false;
        this._paused = false;
        this._keepaliveEl = null;
        this._keepaliveHandle = null;
    }
    /**
     * Create a shared AudioContext inside a user gesture, wire up both
     * TTS and Music GainNodes, and claim the iOS audio session.
     */
    primeAudio() {
        if (this._primed)
            return;
        // Reuse pre-created AudioContext from user gesture if available.
        // On iOS, AudioContext must be created (or resumed) inside a synchronous
        // click handler — but startTour() is async (pool acquire + init break
        // the gesture chain). The app pre-creates one on the START button click
        // and stores it in window.__preCreatedAudioCtx for us to pick up here.
        const preCtx = window.__preCreatedAudioCtx;
        if (preCtx) {
            this._ctx = preCtx;
            window.__preCreatedAudioCtx = undefined;
        }
        else {
            this._ctx = new (window.AudioContext || window.webkitAudioContext)();
        }
        this._gain = this._ctx.createGain();
        // Tap an AnalyserNode between the gain and the destination so the
        // UI can read REAL audio samples flowing through the pipeline. This
        // is the only way to tell the user "your waveform is honest" — when
        // bars stay flat, there is genuinely no audio leaving the browser.
        this._analyser = this._ctx.createAnalyser();
        this._analyser.fftSize = 64;
        this._analyser.smoothingTimeConstant = 0.6;
        this._gain.connect(this._analyser);
        this._analyser.connect(this._ctx.destination);
        // Share the context with MusicPlayer so both use the same audio session
        this._music.setAudioContext(this._ctx);
        this._ctx.resume().catch(() => { });
        // iOS keepalive
        this._keepaliveEl = new Audio();
        const silentMp3 = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAGAAGn9AAAIAAANIAAAARMQg==';
        this._keepaliveEl.src = silentMp3;
        this._keepaliveEl.play().catch(() => { });
        this._keepaliveHandle = window.setInterval(() => {
            if (this._keepaliveEl && !this._queue.isPlaying) {
                this._keepaliveEl.src = silentMp3;
                this._keepaliveEl.play().catch(() => { });
            }
        }, TtsPlayer.KEEPALIVE_INTERVAL_MS);
        this._primed = true;
        this._bus.emit('tts:primed', {
            reused: !!preCtx,
            state: this._ctx.state,
            sampleRate: this._ctx.sampleRate,
        });
    }
    /** The shared AudioContext (available after primeAudio) */
    get audioContext() {
        return this._ctx;
    }
    /** AnalyserNode tap on the live audio pipeline. Read frequency data to
     *  visualise the REAL signal, not a fake JS-timer animation. Returns
     *  null until primeAudio() has been called. */
    get analyser() {
        return this._analyser;
    }
    /** Whether TTS is currently playing or has pending items */
    get isActive() {
        return this._queue.isPlaying || this._queue.pendingCount > 0;
    }
    /** Fetch TTS MP3 without enqueuing. Returns raw ArrayBuffer. */
    async fetchAudio(text, signal) {
        const label = text.length > 30 ? text.slice(0, 30) + '…' : text;
        this._bus.emit('tts:fetch-start', { label, ctxState: this._ctx?.state || 'no-ctx' });
        const res = await fetch(`${this._config.cloudApiBootstrap}/api/tts-speak`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text }),
            signal,
        });
        if (!res.ok)
            throw new Error(`tts-speak HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        this._bus.emit('tts:fetch-done', { label, bytes: buf.byteLength });
        return buf;
    }
    /** Enqueue a pre-fetched MP3 buffer for playback (e.g. from cache) */
    speakBuffer(buffer, label = 'cached') {
        this._paused = false;
        const item = { url: '', label };
        item.__buffer = buffer;
        this._enqueue(item);
    }
    /** Fetch TTS MP3 and enqueue for playback */
    async speak(text) {
        if (!text || !text.trim())
            return;
        this._paused = false;
        const label = text.length > 30 ? text.slice(0, 30) + '…' : text;
        this._bus.emit('tts:fetch-start', { label, ctxState: this._ctx?.state || 'no-ctx' });
        try {
            const res = await fetch(`${this._config.cloudApiBootstrap}/api/tts-speak`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text }),
            });
            if (!res.ok)
                throw new Error(`tts-speak HTTP ${res.status}`);
            const arrayBuf = await res.arrayBuffer();
            this._bus.emit('tts:fetch-done', { label, bytes: arrayBuf.byteLength });
            const item = { url: '', label };
            item.__buffer = arrayBuf;
            this._enqueue(item);
        }
        catch (err) {
            this._bus.emit('tts:error', {
                message: err instanceof Error ? err.message : String(err),
            });
        }
    }
    _enqueue(item) {
        if (this._paused)
            return; // Don't enqueue while paused
        const { dropped } = this._queue.enqueue(item);
        if (dropped) {
            this._bus.emit('tts:dropped', { label: dropped.label });
        }
        if (!this._queue.isPlaying) {
            this._advance();
        }
    }
    _advance() {
        if (this._paused)
            return;
        if (this._queue.pendingCount === 0) {
            this._queue.setPlaying(false);
            // Un-duck music
            this._music.duck(false);
            this._bus.emit('tts:idle');
            return;
        }
        if (!this._ctx || !this._gain) {
            this._bus.emit('tts:error', { message: 'no AudioContext (call primeAudio() first)' });
            this._queue.clearPending();
            return;
        }
        const next = this._queue.dequeue();
        if (!next) {
            this._queue.setPlaying(false);
            this._music.duck(false);
            return;
        }
        const buffer = next.__buffer;
        if (!buffer) {
            this._bus.emit('tts:error', { message: 'TTS queue item has no audio buffer' });
            this._advance();
            return;
        }
        this._queue.setPlaying(true);
        // Duck music (smooth ramp to 15%) — no pause needed anymore
        this._music.duck(true);
        void this._decodeAndPlay(buffer, next.label);
    }
    async _decodeAndPlay(buffer, label) {
        // Keep a copy of the raw MP3 for archival upload
        const mp3Copy = buffer.slice(0);
        try {
            // Ensure AudioContext is running — Safari can suspend after mic use
            if (this._ctx.state === 'suspended') {
                await this._ctx.resume();
            }
            // Double-check: resume() can resolve but context stays suspended on iOS
            if (this._ctx.state !== 'running') {
                this._bus.emit('tts:audio-blocked', {
                    state: this._ctx.state,
                    message: 'AudioContext not running after resume — tap screen to fix',
                });
                // Still try to play — sometimes it works despite state
            }
            const audioBuffer = await this._ctx.decodeAudioData(buffer.slice(0));
            const source = this._ctx.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(this._gain);
            // Fade-in
            this._gain.gain.setValueAtTime(0, this._ctx.currentTime);
            this._gain.gain.linearRampToValueAtTime(1.0, this._ctx.currentTime + TtsPlayer.FADE_IN_MS / 1000);
            source.onended = () => {
                this._currentSource = null;
                this._bus.emit('tts:end', { mp3: mp3Copy, label });
                this._advance();
            };
            this._currentSource = source;
            source.start(0);
            this._bus.emit('tts:start', {
                label,
                ctxState: this._ctx.state,
            });
        }
        catch (err) {
            this._bus.emit('tts:error', {
                message: `decode/play error: ${err instanceof Error ? err.message : String(err)}`,
                ctxState: this._ctx?.state || 'no-ctx',
            });
            this._bus.emit('tts:end');
            this._advance();
        }
    }
    pause() {
        this._paused = true;
        if (this._currentSource) {
            try {
                this._currentSource.stop();
            }
            catch { }
            this._currentSource = null;
        }
        this._queue.clearPending();
        this._queue.setPlaying(false);
        this._music.duck(false);
    }
    stop() {
        if (this._keepaliveHandle !== null) {
            window.clearInterval(this._keepaliveHandle);
            this._keepaliveHandle = null;
        }
        this._queue.clearPending();
        if (this._currentSource) {
            try {
                this._currentSource.stop();
            }
            catch { }
            this._currentSource = null;
        }
        this._queue.setPlaying(false);
        this._music.duck(false);
    }
}
TtsPlayer.FADE_IN_MS = 1200;
TtsPlayer.KEEPALIVE_INTERVAL_MS = 25000;
//# sourceMappingURL=TtsPlayer.js.map